/* Hallmark · component-scope: material-editor-workflow · theme: Operational Architect · pre-emit critique: P5 H5 E4 S5 R5 V5 · contrast: pass (40–41) · tokens: pass (48) · responsive: pass (34,49) */
import { useRef, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  FileText,
  HelpCircle,
  Link as LinkIcon,
  Loader2,
  Music,
  Settings2,
  Sparkles,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { Button, MediaPlayer } from '@lms/ui';
import type {
  Material,
  MaterialMedia,
  MaterialType,
  StorageMediaObject,
  StorageProvider,
} from '@lms/shared/types';
import {
  useAddMaterialMedia,
  useCreateMaterial,
  useDeleteMaterialMedia,
  useMaterialMedia,
  useUpdateMaterial,
  useUploadMedia,
  useStorageProviders,
} from '../hooks/useMaterials';
import {
  useEvaluations,
  useLinkEvaluationToMaterial,
  useUnlinkEvaluationFromMaterial,
} from '../hooks/useEvaluations';
import { useNavigate } from 'react-router';
import { useLanguage, type Bilingual as BilingualType } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { MaterialDeleteAction } from './MaterialDeleteAction';
import { StoredMediaPicker } from './StoredMediaPicker';
import { apiBaseUrl } from '../lib/api-client';
import { normalizeMediaSource } from '@lms/shared/utils';

interface MaterialModalProps {
  material?: Material;
  flowId: string;
  programId: string;
  sessionId?: string;
  contextLabel?: string;
  initialOrder?: number;
  onSaved?: (material: Material) => void;
  onDeleted?: (materialId: string) => void;
  onClose: () => void;
}

type EditorTab = 'DETAILS' | 'SOURCE' | 'SETTINGS' | 'ASSESSMENT' | 'ASSETS';

const mediaTypes: MaterialType[] = ['VIDEO', 'AUDIO', 'DOCUMENT'];

function createInitialMaterial(
  flowId: string,
  sessionId: string | undefined,
  initialOrder: number | undefined
): Partial<Material> {
  return {
    title: '',
    description: '',
    content: '',
    type: 'VIDEO',
    order: initialOrder ?? 1,
    isRequired: true,
    duration: 0,
    flowId,
    sessionId,
  };
}

function getMediaType(mimeType: string): MaterialMedia['type'] {
  if (mimeType.startsWith('video/')) return 'VIDEO';
  if (mimeType.startsWith('audio/')) return 'AUDIO';
  if (mimeType.startsWith('image/')) return 'IMAGE';
  if (mimeType === 'application/pdf') return 'PDF';
  return 'LINK';
}

function getMediaTypeFromFilename(filename: string): MaterialMedia['type'] {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (['mp4', 'webm', 'mov', 'mkv'].includes(extension ?? '')) return 'VIDEO';
  if (['mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac'].includes(extension ?? '')) return 'AUDIO';
  if (extension === 'pdf') return 'PDF';
  return 'LINK';
}

export function MaterialModal({
  material,
  flowId,
  programId,
  sessionId,
  contextLabel,
  initialOrder,
  onSaved,
  onDeleted,
  onClose,
}: MaterialModalProps) {
  const isEditing = Boolean(material);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const sourceInputRef = useRef<HTMLInputElement>(null);
  const assetInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>('DETAILS');
  const [formData, setFormData] = useState<Partial<Material>>(
    () => material ?? createInitialMaterial(flowId, sessionId, initialOrder)
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedLinkEvalId, setSelectedLinkEvalId] = useState('');
  const [assetLink, setAssetLink] = useState('');
  const [storageProvider, setStorageProvider] = useState<StorageProvider>('CONTABO');

  const { mutate: createMaterial, isPending: isCreating } = useCreateMaterial(programId);
  const { mutate: updateMaterial, isPending: isUpdating } = useUpdateMaterial(programId);
  const { mutate: uploadMedia, isPending: isUploading } = useUploadMedia();
  const { data: programEvaluations } = useEvaluations({ programId });
  const { mutate: linkEvaluation } = useLinkEvaluationToMaterial();
  const { mutate: unlinkEvaluation } = useUnlinkEvaluationFromMaterial();
  const { data: mediaLibrary } = useMaterialMedia(material?.id ?? '');
  const { mutate: addMedia, isPending: isAddingMedia } = useAddMaterialMedia(material?.id ?? '');
  const { mutate: deleteMedia } = useDeleteMaterialMedia(material?.id ?? '');
  const { data: storageProviders } = useStorageProviders();

  const materialType = formData.type ?? 'VIDEO';
  const requiresSource = mediaTypes.includes(materialType);
  const isPending = isCreating || isUpdating || isUploading || isAddingMedia;
  const unlinkedEvaluations = programEvaluations?.filter(e => !e.materialId) ?? [];
  const contextName =
    contextLabel ?? material?.session?.title ?? material?.flow?.name ?? sessionId ?? flowId;
  const storageProviderAvailable =
    storageProviders?.find(item => item.provider === storageProvider)?.available ?? true;

  const typeOptions: { value: MaterialType; label: BilingualType; icon: React.ReactNode }[] = [
    { value: 'VIDEO', label: copy.materialModal.typeVideo, icon: <Video size={18} /> },
    { value: 'AUDIO', label: copy.materialModal.typeAudio, icon: <Music size={18} /> },
    { value: 'DOCUMENT', label: copy.materialModal.typeDocument, icon: <FileText size={18} /> },
    { value: 'QUIZ', label: copy.materialModal.typeQuiz, icon: <HelpCircle size={18} /> },
    { value: 'REFLECTION', label: copy.materialModal.typeReflection, icon: <BookOpen size={18} /> },
    { value: 'DISCUSSION', label: copy.materialModal.typeDiscussion, icon: <Sparkles size={18} /> },
  ];

  const tabs: { id: EditorTab; label: BilingualType; icon: React.ReactNode; hidden?: boolean }[] = [
    { id: 'DETAILS', label: copy.materialModal.tabDetails, icon: <BookOpen size={15} /> },
    { id: 'SOURCE', label: copy.materialModal.tabSource, icon: <LinkIcon size={15} /> },
    { id: 'SETTINGS', label: copy.materialModal.tabSettings, icon: <Settings2 size={15} /> },
    {
      id: 'ASSESSMENT',
      label: copy.materialModal.tabAssessment,
      icon: <HelpCircle size={15} />,
      hidden: !isEditing,
    },
    {
      id: 'ASSETS',
      label: copy.materialModal.tabAssets,
      icon: <FileText size={15} />,
      hidden: !isEditing,
    },
  ];

  const handleSourceUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    uploadMedia(
      { file, provider: storageProvider },
      {
        onSuccess: data => setFormData(current => ({ ...current, content: data.url })),
        onError: (error: Error) =>
          setUploadError(error.message || t(copy.materialModal.uploadErrorFallback)),
      }
    );
  };

  const handleAssetUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !material) return;
    setUploadError(null);
    uploadMedia(
      { file, provider: storageProvider },
      {
        onSuccess: data =>
          addMedia({
            materialId: material.id,
            url: data.url,
            type: getMediaType(file.type),
            filename: file.name,
            size: file.size,
            mimeType: file.type,
          }),
        onError: (error: Error) =>
          setUploadError(error.message || t(copy.materialModal.uploadErrorFallback)),
      }
    );
  };

  const handleStoredSource = (media: StorageMediaObject) => {
    setUploadError(null);
    setFormData(current => ({ ...current, content: media.url }));
  };

  const handleStoredAsset = (media: StorageMediaObject) => {
    if (!material) return;
    setUploadError(null);
    addMedia({
      materialId: material.id,
      url: media.url,
      type: getMediaTypeFromFilename(media.filename),
      filename: media.filename,
      size: media.size,
    });
  };

  const handleAttachLink = () => {
    if (!assetLink.trim() || !material) return;
    const url = normalizeMediaSource(assetLink) || assetLink.trim();
    addMedia({
      materialId: material.id,
      url,
      type: 'LINK',
      filename: url.split('/').pop() || t(copy.materialModal.repoUntitled),
    });
    setAssetLink('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const submitData = {
      flowId: sessionId ? undefined : formData.flowId || flowId,
      sessionId: sessionId ?? formData.sessionId,
      title: formData.title,
      description: formData.description,
      type: materialType,
      content: normalizeMediaSource(formData.content) ?? formData.content,
      order: formData.order,
      isRequired: formData.isRequired,
      duration: formData.duration,
    };
    if (isEditing && material) {
      updateMaterial(
        { id: material.id, data: submitData },
        {
          onSuccess: updated => {
            onSaved?.(updated);
            onClose();
          },
        }
      );
      return;
    }
    createMaterial(submitData, {
      onSuccess: created => {
        onSaved?.(created);
        onClose();
      },
    });
  };

  const sourcePlaceholder =
    materialType === 'VIDEO'
      ? copy.materialModal.urlPlaceholderVideo
      : materialType === 'AUDIO'
        ? copy.materialModal.urlPlaceholderAudio
        : copy.materialModal.urlPlaceholderDocument;
  const sourceFormat =
    materialType === 'VIDEO'
      ? copy.materialModal.formatVideo
      : materialType === 'AUDIO'
        ? copy.materialModal.formatAudio
        : copy.materialModal.formatDocument;

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-on-surface/40 p-3 backdrop-blur-sm animate-fadeIn sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="material-editor-title"
      onKeyDown={event => {
        if (event.key === 'Escape') onClose();
      }}
      tabIndex={-1}
    >
      <div className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] bg-surface-container-lowest shadow-level4 animate-slideUp sm:max-h-[calc(100dvh-3rem)]">
        <header className="flex items-start justify-between gap-4 bg-surface-container-low px-5 py-5 sm:px-8 sm:py-6">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                <Bilingual
                  {...(isEditing ? copy.materialModal.modeEdit : copy.materialModal.modeCreate)}
                />
              </span>
              <span className="max-w-full truncate text-[11px] font-body font-medium text-on-surface-variant">
                <Bilingual {...copy.materialModal.learningContext} />: {contextName}
              </span>
            </div>
            <div>
              <h2
                id="material-editor-title"
                className="font-headline text-2xl font-bold tracking-tight text-on-surface sm:text-3xl"
              >
                <Bilingual
                  {...(isEditing ? copy.materialModal.titleEdit : copy.materialModal.titleCreate)}
                />
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
                <Bilingual
                  {...(isEditing
                    ? copy.materialModal.subtitleEdit
                    : copy.materialModal.subtitleCreate)}
                />
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isEditing && material && (
              <MaterialDeleteAction
                material={material}
                programId={programId}
                onDeleted={materialId => {
                  onDeleted?.(materialId);
                  onClose();
                }}
              />
            )}
            <button
              type="button"
              onClick={onClose}
              className="grid size-11 place-items-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-highest hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
              aria-label={t(copy.materialModal.ariaCloseEditor)}
            >
              <X size={20} />
            </button>
          </div>
        </header>

        <nav
          className="flex gap-1 overflow-x-auto bg-surface-container-low px-4 pb-3 sm:px-8"
          aria-label={t(copy.materialModal.editorNavigation)}
        >
          {tabs
            .filter(tab => !tab.hidden)
            .map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={isActive ? 'step' : undefined}
                  className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-3.5 text-[11px] font-label font-bold whitespace-nowrap transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${isActive ? 'bg-surface-container-lowest text-primary shadow-level1' : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'}`}
                >
                  {tab.icon}
                  <Bilingual {...tab.label} />
                </button>
              );
            })}
        </nav>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
            {activeTab === 'DETAILS' && (
              <section
                className="mx-auto max-w-3xl space-y-8"
                aria-labelledby="material-details-heading"
              >
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.materialModal.stepOne} />
                  </p>
                  <h3
                    id="material-details-heading"
                    className="mt-2 font-headline text-xl font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.detailsHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.materialModal.detailsDescription} />
                  </p>
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="material-title"
                    className="block text-xs font-label font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.labelAssetTitle} />
                  </label>
                  <input
                    id="material-title"
                    required
                    aria-required="true"
                    className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    value={formData.title ?? ''}
                    onChange={event =>
                      setFormData(current => ({ ...current, title: event.target.value }))
                    }
                    placeholder={t(copy.materialModal.placeholderTitle)}
                  />
                  <p className="min-h-[1lh] text-[11px] text-on-surface-variant">
                    <Bilingual {...copy.materialModal.titleHelper} />
                  </p>
                </div>
                <fieldset className="space-y-3">
                  <legend className="text-xs font-label font-bold text-on-surface">
                    <Bilingual {...copy.materialModal.labelAssetType} />
                  </legend>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {typeOptions.map(option => {
                      const isSelected = materialType === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setFormData(current => ({ ...current, type: option.value }))
                          }
                          aria-pressed={isSelected}
                          className={`flex min-h-24 flex-col items-start justify-between rounded-2xl p-4 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.98] ${isSelected ? 'bg-primary text-on-primary shadow-level2' : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high'}`}
                        >
                          <span className={isSelected ? 'text-on-primary' : 'text-primary'}>
                            {option.icon}
                          </span>
                          <span className="text-xs font-label font-bold">
                            <Bilingual {...option.label} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <div className="space-y-2">
                  <label
                    htmlFor="material-description"
                    className="block text-xs font-label font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.labelOperationalContext} />
                  </label>
                  <textarea
                    id="material-description"
                    className="min-h-28 w-full resize-y rounded-xl bg-surface-container-high px-4 py-3 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    value={formData.description ?? ''}
                    onChange={event =>
                      setFormData(current => ({ ...current, description: event.target.value }))
                    }
                    placeholder={t(copy.materialModal.placeholderContext)}
                  />
                  <p className="min-h-[1lh] text-[11px] text-on-surface-variant">
                    <Bilingual {...copy.materialModal.descriptionHelper} />
                  </p>
                </div>
              </section>
            )}

            {activeTab === 'SOURCE' && (
              <section
                className="mx-auto max-w-4xl space-y-8"
                aria-labelledby="material-source-heading"
              >
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.materialModal.stepTwo} />
                  </p>
                  <h3
                    id="material-source-heading"
                    className="mt-2 font-headline text-xl font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.sourceHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.materialModal.sourceDescription} />
                  </p>
                </div>
                {requiresSource ? (
                  <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                    <div className="space-y-5 rounded-2xl bg-surface-container-low p-5 sm:p-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="material-source-url"
                          className="block text-xs font-label font-bold text-on-surface"
                        >
                          <Bilingual {...copy.materialModal.labelSourceUrl} />
                        </label>
                        <div className="relative">
                          <LinkIcon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
                          <input
                            id="material-source-url"
                            className="h-12 w-full rounded-xl bg-surface-container-lowest py-3 pl-11 pr-4 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                            value={formData.content ?? ''}
                            onChange={event =>
                              setFormData(current => ({ ...current, content: event.target.value }))
                            }
                            placeholder={t(sourcePlaceholder)}
                          />
                        </div>
                        <p className="min-h-[1lh] text-[11px] text-on-surface-variant">
                          <Bilingual {...copy.materialModal.urlHelper} />
                        </p>
                      </div>
                      <div className="flex items-center gap-3 py-1" aria-hidden="true">
                        <div className="h-px flex-1 bg-on-surface/10" />
                        <span className="text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                          <Bilingual {...copy.materialModal.orUpload} />
                        </span>
                        <div className="h-px flex-1 bg-on-surface/10" />
                      </div>
                      <input
                        ref={sourceInputRef}
                        type="file"
                        className="sr-only"
                        onChange={handleSourceUpload}
                        accept={
                          materialType === 'VIDEO'
                            ? 'video/mp4,video/webm'
                            : materialType === 'AUDIO'
                              ? 'audio/*'
                              : 'application/pdf'
                        }
                      />
                      <div className="space-y-2">
                        <label
                          htmlFor="material-source-storage-provider"
                          className="block text-xs font-label font-bold text-on-surface"
                        >
                          <Bilingual {...copy.materialModal.labelStorageProvider} />
                        </label>
                        <select
                          id="material-source-storage-provider"
                          value={storageProvider}
                          onChange={event =>
                            setStorageProvider(event.target.value as StorageProvider)
                          }
                          className="h-12 w-full rounded-xl bg-surface-container-lowest px-4 text-sm font-body text-on-surface outline-none transition-colors hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                        >
                          <option value="CONTABO">{t(copy.materialModal.providerContabo)}</option>
                          <option value="BIZNETGIO">
                            {t(copy.materialModal.providerBiznetgio)}
                          </option>
                        </select>
                        {!storageProviderAvailable && (
                          <p className="text-xs font-medium text-error">
                            <Bilingual {...copy.materialModal.providerUnavailable} />
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => sourceInputRef.current?.click()}
                        disabled={isUploading || !storageProviderAvailable}
                        className="flex min-h-28 w-full flex-col items-center justify-center gap-3 rounded-2xl bg-surface-container-lowest p-5 text-center text-on-surface transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-55 active:scale-[0.99]"
                      >
                        {isUploading ? (
                          <Loader2 size={22} className="animate-spin text-primary" />
                        ) : (
                          <Upload size={22} className="text-primary" />
                        )}
                        <span className="text-xs font-label font-bold">
                          <Bilingual
                            {...(isUploading
                              ? copy.materialModal.uploading
                              : copy.materialModal.uploadSource)}
                          />
                        </span>
                        <span className="text-[11px] text-on-surface-variant">
                          <Bilingual {...sourceFormat} />
                        </span>
                      </button>
                      <StoredMediaPicker
                        provider={storageProvider}
                        onProviderChange={setStorageProvider}
                        onSelect={handleStoredSource}
                        disabled={isUploading}
                      />
                      {uploadError && (
                        <div
                          role="alert"
                          className="flex gap-3 rounded-xl bg-error/10 p-4 text-error"
                        >
                          <AlertCircle size={18} className="mt-0.5 shrink-0" />
                          <p className="text-xs font-medium leading-relaxed">{uploadError}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex min-h-80 flex-col overflow-hidden rounded-2xl bg-surface-container-low">
                      <div className="flex items-center justify-between gap-3 bg-surface-container-high px-5 py-4">
                        <span className="text-[11px] font-label font-bold uppercase tracking-[0.14em] text-on-surface">
                          <Bilingual {...copy.materialModal.livePreview} />
                        </span>
                        {formData.content && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-success-container px-2.5 py-1 text-[10px] font-label font-bold text-on-success-container">
                            <CheckCircle2 size={13} />
                            <Bilingual {...copy.materialModal.live} />
                          </span>
                        )}
                      </div>
                      <div className="flex flex-1 items-center justify-center bg-on-surface/[0.03] p-5">
                        {formData.content ? (
                          <MediaPlayer
                            height="250px"
                            material={
                              {
                                ...formData,
                                type: materialType,
                                title: formData.title || t(copy.materialModal.previewFallbackTitle),
                              } as Material
                            }
                            apiBaseUrl={apiBaseUrl}
                          />
                        ) : (
                          <div className="max-w-xs text-center">
                            <div className="mx-auto grid size-14 place-items-center rounded-2xl bg-surface-container-high text-primary">
                              {materialType === 'VIDEO' ? (
                                <Video size={26} />
                              ) : materialType === 'AUDIO' ? (
                                <Music size={26} />
                              ) : (
                                <FileText size={26} />
                              )}
                            </div>
                            <p className="mt-4 text-sm font-label font-bold text-on-surface">
                              <Bilingual {...copy.materialModal.previewEmptyTitle} />
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-on-surface-variant">
                              <Bilingual {...copy.materialModal.awaitingMedia} />
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-surface-container-low p-6 sm:p-8">
                    <div className="flex max-w-2xl gap-4">
                      <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-secondary/15 text-secondary">
                        <Sparkles size={20} />
                      </div>
                      <div>
                        <h4 className="font-headline text-lg font-bold text-on-surface">
                          <Bilingual {...copy.materialModal.sourceNotRequiredTitle} />
                        </h4>
                        <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
                          <Bilingual {...copy.materialModal.sourceNotRequiredDescription} />
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {activeTab === 'SETTINGS' && (
              <section
                className="mx-auto max-w-3xl space-y-8"
                aria-labelledby="material-settings-heading"
              >
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.materialModal.stepThree} />
                  </p>
                  <h3
                    id="material-settings-heading"
                    className="mt-2 font-headline text-xl font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.settingsHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.materialModal.settingsDescription} />
                  </p>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label
                      htmlFor="material-duration"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.materialModal.labelDuration} />
                    </label>
                    <input
                      id="material-duration"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body font-medium text-on-surface outline-none transition-colors duration-150 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                      value={formData.duration ?? ''}
                      onChange={event =>
                        setFormData(current => ({
                          ...current,
                          duration: Number.parseInt(event.target.value, 10) || 0,
                        }))
                      }
                      placeholder={t(copy.materialModal.placeholderDuration)}
                    />
                    <p className="min-h-[1lh] text-[11px] text-on-surface-variant">
                      <Bilingual {...copy.materialModal.durationHelper} />
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="material-order"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.materialModal.labelSequenceOrder} />
                    </label>
                    <input
                      id="material-order"
                      type="number"
                      required
                      min="1"
                      inputMode="numeric"
                      className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body font-medium text-on-surface outline-none transition-colors duration-150 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                      value={formData.order ?? 1}
                      onChange={event =>
                        setFormData(current => ({
                          ...current,
                          order: Number.parseInt(event.target.value, 10) || 1,
                        }))
                      }
                    />
                    <p className="min-h-[1lh] text-[11px] text-on-surface-variant">
                      <Bilingual {...copy.materialModal.orderHelper} />
                    </p>
                  </div>
                </div>
                <label className="flex cursor-pointer items-start justify-between gap-5 rounded-2xl bg-surface-container-low p-5 transition-colors duration-150 hover:bg-surface-container-high">
                  <span className="space-y-1">
                    <span className="block text-xs font-label font-bold text-on-surface">
                      <Bilingual {...copy.materialModal.labelMandatoryCompletion} />
                    </span>
                    <span className="block text-xs leading-relaxed text-on-surface-variant">
                      <Bilingual {...copy.materialModal.requiredHelper} />
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formData.isRequired ?? true}
                    onChange={event =>
                      setFormData(current => ({ ...current, isRequired: event.target.checked }))
                    }
                    className="mt-0.5 size-5 shrink-0 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  />
                </label>
              </section>
            )}

            {activeTab === 'ASSESSMENT' && isEditing && material && (
              <section
                className="mx-auto max-w-3xl space-y-8"
                aria-labelledby="material-assessment-heading"
              >
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.materialModal.stepFour} />
                  </p>
                  <h3
                    id="material-assessment-heading"
                    className="mt-2 font-headline text-xl font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.evaluasiMateriTitle} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.materialModal.evaluasiMateriDesc} />
                  </p>
                </div>
                <div className="flex flex-col gap-4 rounded-2xl bg-secondary/10 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex gap-3">
                    <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-secondary/15 text-secondary">
                      <HelpCircle size={21} />
                    </div>
                    <p className="max-w-lg text-sm leading-relaxed text-on-surface-variant">
                      <Bilingual {...copy.materialModal.assessmentCreateHelper} />
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      navigate('/evaluations/create', {
                        state: { programId, materialId: material.id },
                      })
                    }
                    className="h-11 shrink-0 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                  >
                    <Bilingual {...copy.materialModal.evaluasiMateriBtn} />
                    <ChevronRight size={15} />
                  </Button>
                </div>
                {material.evaluations && material.evaluations.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                      <Bilingual {...copy.materialModal.linkedNodes} />
                    </h4>
                    <div className="space-y-2">
                      {material.evaluations.map(evaluation => (
                        <div
                          key={evaluation.id}
                          className="flex items-center gap-3 rounded-xl bg-surface-container-low p-4"
                        >
                          <HelpCircle size={18} className="shrink-0 text-secondary" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-label font-bold text-on-surface">
                              {evaluation.title}
                            </p>
                            <p className="mt-0.5 text-[11px] font-label font-medium uppercase tracking-[0.12em] text-on-surface-variant">
                              {evaluation.type}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => unlinkEvaluation({ evaluationId: evaluation.id })}
                            className="grid size-11 shrink-0 place-items-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-error/10 hover:text-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                            aria-label={`${t(copy.materialModal.unlinkTitle)}: ${evaluation.title}`}
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {unlinkedEvaluations.length > 0 && (
                  <div className="space-y-3 rounded-2xl bg-surface-container-low p-5">
                    <label
                      htmlFor="material-link-evaluation"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.materialModal.linkExisting} />
                    </label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <select
                        id="material-link-evaluation"
                        value={selectedLinkEvalId}
                        onChange={event => setSelectedLinkEvalId(event.target.value)}
                        className="h-12 min-w-0 flex-1 rounded-xl bg-surface-container-lowest px-4 text-sm font-body text-on-surface outline-none transition-colors duration-150 hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                      >
                        <option value="">{t(copy.materialModal.linkSelectPlaceholder)}</option>
                        {unlinkedEvaluations.map(evaluation => (
                          <option key={evaluation.id} value={evaluation.id}>
                            {evaluation.title} ({evaluation.type})
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!selectedLinkEvalId}
                        onClick={() => {
                          if (!selectedLinkEvalId) return;
                          linkEvaluation({
                            evaluationId: selectedLinkEvalId,
                            materialId: material.id,
                          });
                          setSelectedLinkEvalId('');
                        }}
                        className="h-12 shrink-0 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                      >
                        <LinkIcon size={15} />
                        <Bilingual {...copy.materialModal.linkBtn} />
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {activeTab === 'ASSETS' && isEditing && material && (
              <section
                className="mx-auto max-w-3xl space-y-8"
                aria-labelledby="material-assets-heading"
              >
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.materialModal.stepFive} />
                  </p>
                  <h3
                    id="material-assets-heading"
                    className="mt-2 font-headline text-xl font-bold text-on-surface"
                  >
                    <Bilingual {...copy.materialModal.mediaRepoTitle} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.materialModal.mediaRepoSubtitle} />
                  </p>
                </div>
                <div className="grid gap-4 rounded-2xl bg-surface-container-low p-5 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="space-y-2">
                    <label
                      htmlFor="material-asset-link"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.materialModal.labelAssetLink} />
                    </label>
                    <input
                      id="material-asset-link"
                      type="url"
                      value={assetLink}
                      onChange={event => setAssetLink(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAttachLink();
                        }
                      }}
                      placeholder={t(copy.materialModal.mediaRepoPlaceholder)}
                      className="h-12 w-full rounded-xl bg-surface-container-lowest px-4 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                    <label
                      htmlFor="material-asset-storage-provider"
                      className="block pt-2 text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.materialModal.labelStorageProvider} />
                    </label>
                    <select
                      id="material-asset-storage-provider"
                      value={storageProvider}
                      onChange={event => setStorageProvider(event.target.value as StorageProvider)}
                      className="h-12 w-full rounded-xl bg-surface-container-lowest px-4 text-sm font-body text-on-surface outline-none transition-colors hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                    >
                      <option value="CONTABO">{t(copy.materialModal.providerContabo)}</option>
                      <option value="BIZNETGIO">{t(copy.materialModal.providerBiznetgio)}</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAttachLink}
                      disabled={!assetLink.trim() || isAddingMedia}
                      className="h-12 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                    >
                      <LinkIcon size={15} />
                      <Bilingual {...copy.materialModal.attachLink} />
                    </Button>
                    <input
                      ref={assetInputRef}
                      type="file"
                      className="sr-only"
                      onChange={handleAssetUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => assetInputRef.current?.click()}
                      disabled={isUploading || !storageProviderAvailable}
                      className="h-12 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                    >
                      {isUploading ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Upload size={15} />
                      )}
                      <Bilingual {...copy.materialModal.mediaRepoUpload} />
                    </Button>
                    <StoredMediaPicker
                      provider={storageProvider}
                      onProviderChange={setStorageProvider}
                      onSelect={handleStoredAsset}
                      disabled={isUploading || isAddingMedia}
                    />
                  </div>
                </div>
                {uploadError && (
                  <div role="alert" className="flex gap-3 rounded-xl bg-error/10 p-4 text-error">
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <p className="text-xs font-medium leading-relaxed">{uploadError}</p>
                  </div>
                )}
                {mediaLibrary && mediaLibrary.length > 0 ? (
                  <div className="space-y-2">
                    {mediaLibrary.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 rounded-xl bg-surface-container-low p-4"
                      >
                        <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                          <FileText size={18} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-label font-bold text-on-surface">
                            {item.filename || t(copy.materialModal.repoUntitled)}
                          </p>
                          <p className="mt-0.5 text-[11px] text-on-surface-variant">
                            {item.type} · {((item.size ?? 0) / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(t(copy.materialModal.deleteAssetConfirm)))
                              deleteMedia(item.id);
                          }}
                          className="grid size-11 shrink-0 place-items-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-error/10 hover:text-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                          aria-label={`${t(copy.materialModal.deleteAsset)}: ${item.filename || t(copy.materialModal.repoUntitled)}`}
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl bg-surface-container-low p-8 text-center">
                    <FileText size={28} className="mx-auto text-primary" />
                    <p className="mt-3 text-sm font-label font-bold text-on-surface">
                      <Bilingual {...copy.materialModal.repoEmpty} />
                    </p>
                    <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-on-surface-variant">
                      <Bilingual {...copy.materialModal.repoEmptyDesc} />
                    </p>
                  </div>
                )}
              </section>
            )}
          </div>
          <footer className="flex flex-col-reverse gap-3 bg-surface-container-low px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <p className="text-xs leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.materialModal.saveHelper} />
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="h-11 rounded-xl px-4 text-xs font-label font-bold text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
              >
                <Bilingual {...copy.materialModal.abortBtn} />
              </button>
              <Button
                type="submit"
                disabled={isPending}
                className="h-11 rounded-xl px-5 text-xs font-label font-bold whitespace-nowrap active:scale-95"
              >
                {isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                <Bilingual
                  {...(isEditing ? copy.materialModal.submitEdit : copy.materialModal.submitCreate)}
                />
              </Button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}
